<template>
  <div>
    <span class="d-flex mx-4 my-4">
      <v-btn
        large
        :color="isGenomicCheck?'success':'tertiary'"
        class="white--text mt-1 py-6"
        @click="navigateBack()">
        <v-icon large>mdi-chevron-left</v-icon>
      </v-btn>
      <v-spacer />
      <div class="mt-2">
        <div class="d-flex flex-wrap">
          <v-icon class="text-h4">mdi-barn</v-icon>
          <span class="text-h5 ml-2 mt-2">Farm: {{settings.currentFarm}}</span>
        </div>
      </div>
      <v-spacer />
      <v-btn
        fab
        :color="isGenomicCheck?'success':'tertiary'"
        @click="addGroup()">
        <v-icon class="white--text text-h4">mdi-playlist-plus</v-icon>
      </v-btn>
    </span>

    <div class="d-flex flex-wrap justify-center">
      <v-card v-if="filteredBleedGroups.length === 0"
        class="ma-2 pa-10"
        width="356"
        color="cardbackground">
        <div>No Groups found.</div>
      </v-card>
      <v-card v-for="(item, index) in filteredBleedGroups" :key="item.groupName"
          class="ma-2"
          max-width="356"
          color="cardbackground"
        >
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-h6 mb-0">
              <span>
                <v-btn icon @click="toggleCardDetails(index)">
                  <v-icon>{{ showDetail[index] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </span>
              <a @click="toggleCardDetails(index)">{{ item.groupName }}</a>
            </v-list-item-title>
            <v-list-item-subtitle class="ml-9">Sample Count: {{item.samples ? item.samples.length : 0}}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-card-actions class="pb-4">
          <v-btn
            x-large
            :color="isGenomicCheck?'success':'tertiary'"
            class="ml-1 pl-4 pr-6"
            @click="goToSamplesPage(item)">
            <div v-if="isGenomicCheck">
              <v-icon large>mdi-check-decagram-outline</v-icon>
              <span class="ml-1 mt-1">Check</span>
            </div>
            <div v-else class="white--text">
              <v-icon large>mdi-dna</v-icon>
              <span class="ml-1 mt-1">Bleed</span>
            </div>
          </v-btn>

          <v-btn
            fab
            class="ml-2"
            color="primary"  
            :disabled="isUploadDisabled(item)"
            @click="selectedGroups[0]=item.groupName; showUploadDialog = true">
            <v-icon>mdi-upload</v-icon>
          </v-btn>

          <v-btn
            fab
            color="success"
            @click="editGroup(item)">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>

          <v-btn
              fab
              color="secondary"
              @click="confirmDeleteGroup(item)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </v-card-actions>

        <v-expand-transition>
          <div v-show="showDetail[index]">
            <v-divider></v-divider>

            <v-card-text>
              <div class="primary--text text-subtitle-1">Details</div>
              <ul class="side-by-side">
                <li><b>Operator:</b> {{item.operator}}</li>
                <li><b>Farm:</b> {{item.farm}}</li>
                <li><b>Line:</b> {{item.line}}</li>
                <li><b>Disp:</b> {{item.disp}}</li>
                <li><b>Hatch:</b> {{item.hatch}}</li>
                <li><b>Mini-Gen:</b> {{item.minigeneration}}</li>
                <li><b>TR/EX:</b> {{item.trex}}</li>
                <li><b>Gender:</b> {{item.gender}}</li>
              </ul>
              <div style="clear: both" />
              <ul class="single pb-6">
                <li><b>Notes:</b> {{item.notes}}</li>
              </ul>
            </v-card-text>
          </div>
        </v-expand-transition>
      </v-card>
    </div>

    <v-dialog v-model="showModifyDialog" persistent max-width="400px">
      <v-card color="cardbackground">
        <v-card-title>
          <div class="headline" v-if="editItem.oldGroupName === null">Create Group</div>
          <div class="headline" v-else>Modify Group</div>
        </v-card-title>
        <v-card-text class="py-0">
          <div
            class="d-flex flex-wrap overflow-y-auto"
            style="height: 410px;">
            <v-col cols="6">
              <v-text-field
                class="pa-0"
                v-model="editItem.operator"
                clearable
                label="Operator *"
                @keypress="allowAlphaNumOnly($event)"
                @change="editItem.operator = toAlphaNumOnly(editItem.operator)"
              /> 
            </v-col>
            <v-col cols="6">
              <v-text-field
                class="pa-0"
                disabled
                v-model="editItem.farm"
                label="Farm"
              />
            </v-col>
            <v-col cols="6">
              <v-combobox
                class="pa-0"
                v-model="editItem.line"
                label="Line *"
                :items="lines"
                :disabled="lines.length === 0"
                @keypress="allowNumOnly($event)"
                @keyup="editItem.line = toNumOnly(editItem.line)"
                @change="editItem.line = toNumOnly(editItem.line); lineChanged()"
              />
            </v-col>
            <v-col cols="6">
              <v-combobox
                class="pa-0"
                v-model="editItem.minigeneration"
                label="MG *"
                :items="mgs"
                :disabled="!editItem.line"
                @keypress="allowNumOnly($event)"
                @keyup="editItem.minigeneration = toNumOnly(editItem.minigeneration)"
                @change="editItem.minigeneration = toNumOnly(editItem.minigeneration)"
              />
            </v-col>
              <v-col cols="6">
              <v-select
                class="pa-0"
                v-model="editItem.disp"
                label="Disp *"
                :items="[ 'BR', 'BRF', 'BRM', 'DX', 'FC', 'HAT', 'SIB']"
                @change="dispChanged()"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                class="pa-0"
                v-model="editItem.hatch"
                :label="editItem.disp?.includes('BR') ? 'Hatch' : 'Hatch *'"
                :items="[ '1', '2', '3', '4', '5', '6', '7']"
                :disabled="editItem.disp?.includes('BR')"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                class="pa-0"
                v-model="editItem.trex"
                label="TR/EX"
                :items="[ '', 'EX', 'TR']"
              />
            </v-col>
              <v-col cols="6">
              <v-select
                class="pa-0"
                v-model="editItem.gender"
                label="Gender"
                :items="[ 'F', 'M', 'U']"
                :disabled="['BRF', 'BRM'].includes(editItem.disp)"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                class="pa-0"
                clearable
                v-model="editItem.notes"
                @keypress="allowAlphaNumOnly($event)"
                @keyup="editItem.notes = toAlphaNumOnly(editItem.notes)"
                label="Notes"
              />
            </v-col>
          </div>
        </v-card-text>
        <v-card-actions class="py-0">
          <div class="flex-grow-1"></div>
          <v-btn
            color="primary"
            class="white--text mb-3"
            :disabled="!saveEnabled"
            @click="editItem.oldGroupName != null ? showModifyConfirmDialog = true : saveGroup()">
            Save
          </v-btn>
          <v-btn
            color="secondary"
            class="mb-3 mr-4"
            @click="closeModifyDialog()">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

     <v-dialog v-model="showModifyConfirmDialog" persistent max-width="350">
      <v-card color="cardbackground">
        <v-card-title class="text-h5">Save Group Changes?</v-card-title>
        <v-card-text class="d-flex">
          <div>
            <div class="primary--text text-subtitle-1">Old</div>
            <ul class="pl-4 mr-2" style="min-width:145px; border-top:1px solid">
              <li><b>Operator:</b> {{oldModifyItem.operator}}</li>
              <li><b>Farm:</b> {{oldModifyItem.farm}}</li>
              <li><b>Line:</b> {{oldModifyItem.line}}</li>
              <li><b>Disp:</b> {{oldModifyItem.disp}}</li>
              <li><b>Hatch:</b> {{oldModifyItem.hatch}}</li>
              <li><b>Mini-Gen:</b> {{oldModifyItem.minigeneration}}</li>
              <li><b>TR/EX:</b> {{oldModifyItem.trex}}</li>
              <li><b>Gender:</b> {{oldModifyItem.gender}}</li>
              <li><b>Notes:</b> {{oldModifyItem.notes}}</li>
            </ul>
          </div>
          <v-spacer />
          <div class="ml-2">
            <div class="primary--text text-subtitle-1">New</div>
            <ul class="pl-4 mr-2" style="min-width:145px; border-top:1px solid">
              <li :style="changeBackgroundColor(editItem.operator != oldModifyItem.operator)"><b>Operator:</b> {{editItem.operator}}</li>
              <li :style="changeBackgroundColor(editItem.farm != oldModifyItem.farm )"><b>Farm:</b> {{editItem.farm}}</li>
              <li :style="changeBackgroundColor(editItem.line != oldModifyItem.line )"><b>Line:</b> {{editItem.line}}</li>
              <li :style="changeBackgroundColor(editItem.disp != oldModifyItem.disp )"><b>Disp:</b> {{editItem.disp}}</li>
              <li :style="changeBackgroundColor(editItem.hatch != oldModifyItem.hatch)"><b>Hatch:</b> {{editItem.hatch}}</li>
              <li :style="changeBackgroundColor(editItem.minigeneration != oldModifyItem.minigeneration)"><b>Mini-Gen:</b> {{editItem.minigeneration}}</li>
              <li :style="changeBackgroundColor(editItem.trex != oldModifyItem.trex)"><b>TR/EX:</b> {{editItem.trex}}</li>
              <li :style="changeBackgroundColor(editItem.gender != oldModifyItem.gender)"><b>Gender:</b> {{editItem.gender}}</li>
              <li :style="`${changeBackgroundColor(editItem.notes != oldModifyItem.notes)}`"><b>Notes:</b> {{editItem.notes}}</li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="saveGroup()">Save</v-btn>
          <v-btn color="secondary" @click="showModifyConfirmDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSaveConfirmDialog" persistent max-width="350">
      <v-card  color="cardbackground">
        <v-card-title class="text-h5">Confirm Save</v-card-title>
        <v-card-text>
          <div v-if="!lines.find(e => e == editItem.line)">Line({{editItem.line}}) appears to be invalid.</div>
          <div v-else-if="!mgs.find(e => e == editItem.minigeration)">
            MG({{editItem.minigeneration}}) appears to be invalid for line ({{editItem.line}}).
          </div>
          <div>Continue?</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" class="white--text" @click="saveGroup(true)">Yes</v-btn>
          <v-btn color="secondary" @click="showSaveConfirmDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteDialog" persistent max-width="350">
      <v-card  color="cardbackground">
        <v-card-title class="text-h5">Confirm Delete</v-card-title>
        <v-card-text>Is it okay to delete {{ groupToDelete }}?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" class="white--text" @click="deleteGroup()">Yes</v-btn>
          <v-btn color="secondary" @click="showDeleteDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showUploadDialog" persistent max-width="350">
      <v-card  color="cardbackground">
        <v-card-title class="text-h5">Confirm Upload</v-card-title>
        <v-card-text>
          <div>Upload these Genomic Bleed Groups?</div>
          <ul class="mt-2">
            <li v-for="groupName in selectedGroups" :key="groupName">
              {{ groupName }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="uploadGenomicBleedGroups()">Upload</v-btn>
          <v-btn color="secondary" @click="showUploadDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="isBusy">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>
<script>
import OfflineStorage from '../offlineStorage'
import { mapState } from 'vuex'
import gbaApi from '../api/gbaApi'

export default {
  data() {
    return {
      genomicBleedGroups: [],
      isBusy: false,
      archivedGenomicBleedGroups: [],
      groupToDelete: null,
      showModifyDialog: false,
      showModifyConfirmDialog: false,
      showDeleteDialog: false,
      showSaveConfirmDialog: false,
      showUploadDialog: false,
      editItem: {},
      lines: [],
      mgs: [],
      selectedGroups: [],
      showDetail: []
    }
  },

  async created() {
    try {
      this.genomicBleedGroups = await OfflineStorage.getItem('genomicBleedGroups');
      if (this.genomicBleedGroups == null) this.genomicBleedGroups = [];

      this.archivedGenomicBleedGroups = await OfflineStorage.getItem('archivedGenomicBleedGroups');
      if (this.archivedGenomicBleedGroups == null) this.archivedGenomicBleedGroups = [];
      else await this.removeOldGenomicBleedGroups();

      if (this.settings.currentFarm == null) {
        this.$root.showNoticeWarning(
          'Please set the default farm. This can be done from the Settings page.'
        );
      }

    } catch(error) {
      this.$root.showNoticeError(error.message);
    }
  },

  computed: {
    ...mapState(['settings', 'isOnline']),
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    farmLineMg() {
      const flm = this.settings.farmLineMg.filter(item => {
        const dtEnd = new Date(item.mgEndDate);
        const dtStart = new Date(item.mgStartDate);
        const now = new Date();
        return dtStart <= now && dtEnd >= now;
      });
      return flm;
    },
    filteredBleedGroups() {
      let filteredGroups;
      if(this.isGenomicCheck) {
        filteredGroups = this.genomicBleedGroups.filter(x=>x.checkWingband);
      }
      else {
        filteredGroups = this.genomicBleedGroups.filter(x=>!x.checkWingband);
      }
      return filteredGroups;
    },
    isGenomicCheck() {
      let isCheck = this.$route.params.check ? true : false;
      return isCheck;
    },
    saveEnabled() {
      const item = this.editItem;
      let valid = false;
      if (item.operator && item.farm && item.line && item.minigeneration && item.disp)
      {
        if (item.disp?.includes('BR')) valid = true;
        else if (item.hatch) valid = true;
      }
      return valid;
    },
    oldModifyItem() {
      var oldItem = this.genomicBleedGroups.find(x => x.groupName === this.editItem.oldGroupName);
      return oldItem == null ? {} : oldItem;
    }
  },

  methods: {
    addGroup() {
      this.editItem = {
        checkWingband: false,
        oldGroupName: null,
        groupName: null,
        operator: null,
        farm: this.settings.currentFarm,
        line: null,
        minigeneration: null,
        hatch: null,
        trex: '',
        notes: null,
        disp: null,
        samples: []
      };

      this.farmChanged();

      this.showModifyDialog = true;
    },
    allowNumOnly(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if(/^[0-9]+$/.test(char)) return true; // Match with regex 
      else e.preventDefault(); // If not match, don't add to input text
    
    },
    allowAlphaNumOnly(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if(/^[A-Za-z0-9 ]+$/.test(char)) return true; // Match with regex 
      else e.preventDefault(); // If not match, don't add to input text
    },
    changeBackgroundColor(isChanged) {
      if (!isChanged) return '';
      if (this.$vuetify.theme.dark)
        return 'background-color: gray';
      else
        return 'background-color:yellow';
    },
    closeModifyDialog() {
      this.showModifyDialog = false;
      this.showModifyConfirmDialog = false;
    },
    confirmDeleteGroup(item) {
      this.showDeleteDialog = true;
      this.groupToDelete = item.groupName;
    },
    daysDiff(firstDate, secondDate) {
      const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
      const diffDays = Math.round(Math.abs((new Date(firstDate) - new Date(secondDate)) / oneDay));
      return diffDays;
    },
    async deleteGroup() {
      try {
        const index = this.genomicBleedGroups.findIndex(x => x.groupName === this.groupToDelete);

        //* Add group to archived groups.
        this.genomicBleedGroups[index].deleteDate = new Date();
        this.archivedGenomicBleedGroups.push(this.genomicBleedGroups[index]);
        await OfflineStorage.setItem('archivedGenomicBleedGroups', this.archivedGenomicBleedGroups);

        //* Remove group from active groups.
        this.genomicBleedGroups.splice(index, 1);
        await OfflineStorage.setItem('genomicBleedGroups', this.genomicBleedGroups);

        this.$root.showNoticeSuccess('Group Deleted Successfully');
        this.showDeleteDialog = false;
      } catch (error) {
        this.$root.showNoticeError(error.message);
      }
    },
    async dispChanged() {
       if (this.editItem.disp?.includes('BR')) {
          this.editItem.hatch = undefined;
          if (this.editItem.disp == 'BRF' || this.editItem.disp == 'BRM') {
            this.editItem.gender = undefined;
          }
          await this.$root.waitAsync(2);   // wait for next tick 
       }
    },
    editGroup(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.editItem.oldGroupName = this.editItem.groupName;
      this.setAvailableLines();
      this.setAvailableMgs();
      this.showModifyDialog = true;
    },
    farmChanged() {
      this.editItem.line = null;
      this.editItem.minigeneration = null;
      this.setAvailableLines();
      this.setAvailableMgs();
    },
    goToSamplesPage(item) {
      if (this.isGenomicCheck)
        this.$router.push({ name: 'Genomic Checks',  params: {check: true, groupName: item.groupName}});
      else
        this.$router.push({ name: 'Genomic Bleeds', params: {groupName: item.groupName}});
    },
    isValidDate(d) {
      return d instanceof Date && !isNaN(d);
    },
    isUploadDisabled(item) {
      if (!this.isOnline) return true;
      if (item.samples == null) return true;
      if (item.samples.length === 0) return true;
      return false;
    },
    async saveGroup(overrideConfirm) {
      try {

        if (this.saveEnabled == false) {
          this.$root.showNoticeError("Please enter required fields.");
          this.showModifyConfirmDialog = false;
          this.showSaveConfirmDialog = false;
          return;
        }
        if (overrideConfirm == undefined || overrideConfirm == false) {
          let foundLine = this.lines.find(e => e == this.editItem.line);
          let foundMg = this.mgs.find(e => e == this.editItem.minigeneration);
          if (!foundLine || !foundMg) {
            this.showSaveConfirmDialog = true;
            return;
          }
        }
        this.showSaveConfirmDialog = false;

        let groupName = `${this.editItem.farm}_${this.editItem.line}_MG${this.editItem.minigeneration}`;
        if (this.editItem.hatch) groupName += `_H${this.editItem.hatch}`;
        groupName += `_${this.editItem.disp}`; 
        if (this.editItem.trex.length > 0) groupName = `${this.editItem.trex}_${groupName}`;

        const genomicBleedGroup = {
          groupName: groupName,
          checkWingband: this.isGenomicCheck,
          operator: this.editItem.operator.toUpperCase(),
          farm: this.editItem.farm,
          line: this.editItem.line,
          minigeneration: this.editItem.minigeneration,
          hatch: this.editItem.hatch,
          disp: this.editItem.disp,
          trex: this.editItem.trex,
          notes: this.editItem.notes ? this.editItem.notes: '',
          gender: this.editItem.gender ? this.editItem.gender : '',
          samples: this.editItem.samples
        }

        if (this.editItem.oldGroupName == null) {
          const items = this.genomicBleedGroups.filter(x => x.groupName === groupName);
          if (items.length > 0) {
            throw new Error(`Duplicate Group. ${groupName} already exists.`);
          }
          this.genomicBleedGroups.push(genomicBleedGroup);
        }
        else {
          const items = this.genomicBleedGroups.filter(x => x.groupName === groupName);
          if (this.editItem.oldGroupName !== groupName && items.length > 0) {
            throw new Error(`Duplicate Group. ${groupName} already exists.`);
          }
          const itemIndex = this.genomicBleedGroups.findIndex(x => x.groupName === this.editItem.oldGroupName);
          this.genomicBleedGroups[itemIndex] = genomicBleedGroup;
          // deep clone to force computed values to reset
          this.genomicBleedGroups = JSON.parse(JSON.stringify(this.genomicBleedGroups));
        }
        await OfflineStorage.setItem('genomicBleedGroups', this.genomicBleedGroups);
        this.editItem = { trex: '' };
        this.closeModifyDialog();
      } catch(error) {
        this.$root.showNoticeError(error.message);
      }
    },
    lineChanged() {
      this.editItem.minigeneration = null;
      this.setAvailableMgs();
    },
    navigateBack() {
      this.$router.replace({ path: '/' });
    },
    async removeOldGenomicBleedGroups() {
      let numToRemove = 0;

      //* Filter archive files (remove files older than 45 days based on
      //* the latest upload date or delete date.
      const oldGroups = this.archivedGenomicBleedGroups.filter(group => {
        let now =  new Date();
        const MIN_DATE = new Date(-8640000000000000);
        let uploadDate = new Date(group.uploadDate);
        let deleteDate = new Date(group.deleteDate);
        let purgeDate = this.isValidDate(uploadDate) ? uploadDate: MIN_DATE;
        if (purgeDate < deleteDate)
          purgeDate = deleteDate;

        let diffDays = this.daysDiff(now, group.uploadDate);
        if (diffDays > 45) {
          numToRemove ++;
          return false;
        }
        return true;
      });

      if (numToRemove > 0) {
        this.archivedGenomicBleedGroups = oldGroups;
        await OfflineStorage.setItem('archivedGenomicBleedGroups', this.archivedGenomicBleedGroups);
      }

      console.log(`Removed ${numToRemove} old Genomic Bleed Groups.`);
    },
    setAvailableLines() {
      const farmLines = this.farmLineMg.filter(e => e.farm == this.editItem.farm);
      if (farmLines == null) this.lines = [];
      else this.lines = [...new Set(farmLines.map(x=> x.line))];
    },
    setAvailableMgs() {
      const farmLine = this.farmLineMg.find(e => e.farm == this.editItem.farm && e.line==this.editItem.line);
      if (farmLine == null) this.mgs = [];
      else {
        let mgs = [];
        const mgStart = parseInt(farmLine.mgStart);
        const mgEnd = parseInt(farmLine.mgEnd);
        for(var i = mgStart; i<= mgEnd; i++) mgs.push(i);
        this.mgs = mgs.sort((a,b,) => (a>b ? -1: 1)); // sort in desc order.
      }
    },
    toAlphaNumOnly(value) {
      if (value == null) return;
      value = value.toUpperCase();
      value = value.replace(/[^A-Z0-9\s!?]/g,'');
      return value;
    },
    toggleCardDetails(index) {
      this.showDetail[index] = !this.showDetail[index];
      this.$forceUpdate();
    },
    toNumOnly(value) {
      if (!value) return null;
      if (Number.isInteger(value)) return value;
      let newValue = value.replace(/[^0-9!?]/g,'');
      return parseInt(newValue);
    },
    async uploadGenomicBleedGroups() {
      try {
        this.isBusy = true;
        this.showUploadDialog = false;
        await this.removeOldGenomicBleedGroups();

        let payload = [];
        let numGroupsUploaded = 0;

        const bleedGroups = [];
        this.selectedGroups.forEach(groupName => 
          bleedGroups.push(this.genomicBleedGroups.find(e => e.groupName === groupName))
        );

        for (let i = 0; i < bleedGroups.length; i++) {
          for (let j = 0; j < bleedGroups[i].samples.length; j++) {  
            payload.push({
              groupName: bleedGroups[i].groupName,
              operator: bleedGroups[i].operator,
              farm: bleedGroups[i].farm,
              line: bleedGroups[i].line + "", // convert to string
              minigeneration: bleedGroups[i].minigeneration,
              hatch: bleedGroups[i].hatch,
              disp: bleedGroups[i].disp,
              trex: bleedGroups[i].trex,
              notes: bleedGroups[i].notes,
              gender: bleedGroups[i].gender,
              wingband: bleedGroups[i].samples[j].wingband,
              tube: bleedGroups[i].samples[j].tube,
              date: bleedGroups[i].samples[j].date
            });
          }

          var uploadDateTime = await gbaApi.postGenomicBleed(payload);
          numGroupsUploaded ++;

          //* Add group to archived groups.
          //bleedGroups[i].uploadDate = new Date();
          bleedGroups[i].uploadDate = uploadDateTime;
          this.archivedGenomicBleedGroups.push(bleedGroups[i]);
          await OfflineStorage.setItem('archivedGenomicBleedGroups', this.archivedGenomicBleedGroups);

          //* Remove group from active groups.
          const ndx = this.genomicBleedGroups.findIndex(s => s.groupName == bleedGroups[i].groupName);
          this.genomicBleedGroups.splice(ndx, 1);
          await OfflineStorage.setItem('genomicBleedGroups', this.genomicBleedGroups);

          payload = [];
        }
        this.isBusy = false;
        if (numGroupsUploaded > 0) this.$root.showNoticeSuccess(`${numGroupsUploaded} Genomic Bleed Groups uploaded successfully.`);
      } catch(error) {
        console.log(error);
        this.$root.showNoticeError(error.message);
        this.isBusy = false;
      }
    }
  }
}
</script>
<style scoped>
  .side-by-side li {
    display: block;
    float: left;
    width: 140px;
    margin: 2px;
  }
  .single li {
    display: block;
    float: left;
    margin: 2px;
  }
</style>